import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { fetchEnrichedEvents } from './analyticsApi';
import { countDistinctSessions } from './utils/countDistinctSessions';
import type { EnrichedEventOut } from './analyticsTypes';
import { readAxiosDetail } from '../api_calls';
import { trackAnalyticsEvent } from '../../../services/analytics';
import KpiTile from './components/KpiTile';
import RankedList from './components/RankedList';
import EmptyState from './components/EmptyState';
import { useInspector } from './hooks/useInspector';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors } from '../../../theme/opsTokens';

const POLL_INTERVAL_MS = 10_000;
const WINDOW_MS = 30 * 60 * 1000;
const MAX_FEED_ROWS = 50;
const MAX_FETCH_ROWS = 200;
const SIMULATION_COMPLETE_EVENT = 'simulation_run_complete';

function isLegacyName(name: string): boolean {
  return name.startsWith('legacy.');
}

export default function RealtimeSection() {
  const inspector = useInspector();
  const { filters, setFilters } = useAnalyticsFilters();
  const [events, setEvents] = useState<EnrichedEventOut[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [paused, setPaused] = useState(false);
  const visibilityPausedRef = useRef(false);
  const isMountedRef = useRef(true);

  const poll = useCallback(async () => {
    if (visibilityPausedRef.current) return;
    const since = new Date(Date.now() - WINDOW_MS).toISOString();
    try {
      const res = await fetchEnrichedEvents({ since, page: 1, page_size: MAX_FETCH_ROWS });
      if (!isMountedRef.current) return;
      setEvents(res.items);
      setLastUpdated(new Date());
      setError(null);
      setLoading(false);
    } catch (e) {
      if (!isMountedRef.current) return;
      setError(readAxiosDetail(e));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void poll();
    return () => {
      isMountedRef.current = false;
    };
  }, [poll]);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [paused, poll]);

  useEffect(() => {
    const onVisibilityChange = () => {
      visibilityPausedRef.current = document.visibilityState === 'hidden';
      if (!visibilityPausedRef.current) void poll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [poll]);

  // ----- Derived -----
  const filtered = useMemo(() => {
    if (!filters.hideLegacy) return events;
    return events.filter((e) => !isLegacyName(e.received_event_name));
  }, [events, filters.hideLegacy]);

  const distinctUsers = useMemo(() => {
    const set = new Set<string>();
    for (const e of filtered) {
      if (e.username) set.add(e.username);
    }
    return set.size;
  }, [filtered]);

  const distinctSessions = useMemo(
    () => countDistinctSessions(filtered),
    [filtered],
  );

  const topEventName = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filtered) counts.set(e.received_event_name, (counts.get(e.received_event_name) ?? 0) + 1);
    let best: string | null = null;
    let bestCount = 0;
    counts.forEach((count, name) => {
      if (count > bestCount) {
        best = name;
        bestCount = count;
      }
    });
    return best ? `${best} (${bestCount})` : '—';
  }, [filtered]);

  const simulationCompletions = useMemo(
    () => filtered.filter((e) => e.received_event_name === SIMULATION_COMPLETE_EVENT).length,
    [filtered],
  );

  const topScreen = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filtered) {
      if (e.screen) counts.set(e.screen, (counts.get(e.screen) ?? 0) + 1);
    }
    let best: string | null = null;
    let bestCount = 0;
    counts.forEach((count, name) => {
      if (count > bestCount) {
        best = name;
        bestCount = count;
      }
    });
    return best ? `${best} (${bestCount})` : '—';
  }, [filtered]);

  const topEventItems = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filtered) counts.set(e.received_event_name, (counts.get(e.received_event_name) ?? 0) + 1);
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ id: name, primary: name, value: count }));
  }, [filtered]);

  const topCustomerItems = useMemo(() => {
    const counts = new Map<string, { count: number; label: string }>();
    for (const e of filtered) {
      const id = e.customer_name ?? 'Unknown';
      const cur = counts.get(id) ?? { count: 0, label: id };
      cur.count += 1;
      counts.set(id, cur);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .map(([id, v]) => ({ id, primary: v.label, value: v.count }));
  }, [filtered]);

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status bar */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          icon={<Icon d={ICON_FALLBACKS.Bolt} size={20} />}
          label={paused ? 'Paused' : 'Live'}
          color={paused ? 'default' : 'success'}
          size="small"
          variant={paused ? 'outlined' : 'filled'}
        />
        <Typography variant="caption" color="text.secondary">
          Window: last 30 min · {lastUpdated ? `updated ${lastUpdated.toLocaleTimeString()}` : 'loading…'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={paused ? 'Resume polling' : 'Pause polling'} arrow>
          <IconButton size="small" onClick={() => {
            const next = !paused;
            trackAnalyticsEvent('admin_analytics_realtime_paused', {
              paused: next,
              reason: 'manual',
              timestamp: new Date().toISOString(),
            });
            setPaused(next);
          }}>
            {paused ? <Icon name="play" size={20} /> : <Icon name="pause" size={20} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Refresh now" arrow>
          <IconButton size="small" onClick={() => void poll()}>
            <Icon name="arrows-rotate" size={20} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* KPI strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
        }}
      >
        <KpiTile icon={<Icon name="clock-rotate-left" size={20} />} label="Sessions (last 30m)" subtitle="Website visits" value={distinctSessions} loading={loading} />
        <KpiTile icon={<Icon name="users" size={20} />} label="Users (last 30m)" value={distinctUsers} loading={loading} />
        <KpiTile icon={<Icon name="calendar-day" size={20} />} label="Events (last 30m)" value={filtered.length} loading={loading} />
        <KpiTile icon={<Icon d={ICON_FALLBACKS.PlayCircleOutline} size={20} />} label="Sim runs (last 30m)" value={simulationCompletions} loading={loading} />
        <KpiTile icon={<Icon d={ICON_FALLBACKS.Leaderboard} size={20} />} label="Top event" value={topEventName} loading={loading} />
        <KpiTile icon={<Icon d={ICON_FALLBACKS.Monitor} size={20} />} label="Top screen" value={topScreen} loading={loading} />
      </Box>

      {/* Live feed + ranked sidebars */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Card variant="outlined">
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon d={ICON_FALLBACKS.Bolt} size={20} />
                <Typography variant="subtitle1" fontWeight={700}>Live event feed</Typography>
              </Stack>
            }
            subheader={`Showing ${Math.min(filtered.length, MAX_FEED_ROWS)} of ${filtered.length}`}
            subheaderTypographyProps={{ variant: 'caption' }}
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 1, maxHeight: 520, overflow: 'auto' }}>
            {loading ? (
              <Stack spacing={0.75}>
                {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={42} />)}
              </Stack>
            ) : filtered.length === 0 ? (
              <EmptyState
                title="No events in the last 30 minutes"
                body={filters.hideLegacy ? 'Legacy events are hidden — toggle them on in Events Explorer to include them.' : undefined}
                compact
              />
            ) : (
              <Stack spacing={0.5} divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                {filtered.slice(0, MAX_FEED_ROWS).map((e, idx) => {
                  const legacy = isLegacyName(e.received_event_name);
                  const isSimComplete = e.received_event_name === SIMULATION_COMPLETE_EVENT;
                  return (
                    <Box
                      key={`${e.event_created_at}-${idx}`}
                      onClick={() => inspector.open({ kind: 'event', event: e })}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          inspector.open({ kind: 'event', event: e });
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Inspect event ${e.received_event_name}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '70px 1fr', sm: '110px 1fr auto' },
                        columnGap: { xs: 1, sm: 1.5 },
                        alignItems: 'center',
                        py: 0.75,
                        px: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: isSimComplete ? alpha(opsColors.semantic.success.color, 0.08) : 'transparent',
                        borderLeft: isSimComplete ? `3px solid ${opsColors.semantic.success.color}` : '3px solid transparent',
                        '&:hover': { backgroundColor: isSimComplete ? alpha(opsColors.semantic.success.color, 0.16) : 'action.hover' },
                        '&:focus-visible': { outline: `2px solid ${opsColors.brand.primary}`, outlineOffset: -2 },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {new Date(e.event_created_at).toLocaleTimeString()}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        {isSimComplete && (
                          <Icon d={ICON_FALLBACKS.PlayCircleOutline} size={20} style={{ color: '#2e7d32', flexShrink: 0 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: legacy ? 'text.disabled' : isSimComplete ? 'success.dark' : 'text.primary',
                            fontWeight: isSimComplete ? 600 : 400,
                          }}
                        >
                          {e.received_event_name}
                        </Typography>
                        {isSimComplete && (
                          <Chip label="Sim run" size="small" color="success" variant="filled" sx={{ height: 18, fontSize: 10 }} />
                        )}
                        {legacy && (
                          <Chip label="Legacy" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 160,
                        }}
                      >
                        {e.username ?? e.customer_name ?? '—'}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Stack spacing={3}>
          <RankedList
            title="Top events"
            icon={<Icon d={ICON_FALLBACKS.Leaderboard} size={20} />}
            items={topEventItems}
            loading={loading}
            limit={8}
            onItemClick={(item) => setFilters({ eventName: item.id, view: 'events' })}
            color={alpha(opsColors.brand.primary, 0.8)}
          />
          <RankedList
            title="Active customers"
            icon={<Icon d={ICON_FALLBACKS.Business} size={20} />}
            items={topCustomerItems}
            loading={loading}
            limit={8}
          />
        </Stack>
      </Box>
    </Stack>
  );
}
